import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useBuildVersion } from '../hooks/useBuildVersion';

/**
 * Self-healing update prompt. When a new portal build is deployed, any open tab
 * detects it and:
 *   - auto-reloads on the next route navigation (seamless — the user is already
 *     leaving the current page, so no in-progress form input is lost), and
 *   - shows a small banner with a Reload button for an immediate update.
 *
 * Rendered once at the app root, inside the router. Theme-neutral inline styles
 * so it looks right over both the light participant portal and the dark platform
 * console.
 */
export default function UpdateGate() {
  const updateAvailable = useBuildVersion();
  const location = useLocation();
  const firstRender = useRef(true);

  // Read the latest flag inside the navigation effect without making the effect
  // re-run when the flag flips (we only want to react to navigation).
  const pendingRef = useRef(false);
  pendingRef.current = updateAvailable;

  useEffect(() => {
    if (firstRender.current) { firstRender.current = false; return; }
    if (pendingRef.current) window.location.reload();
  }, [location.pathname]);

  if (!updateAvailable) return null;

  return (
    <div
      role="status"
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px',
        padding: '8px 16px', fontSize: '14px', color: '#fff',
        background: '#2E6CB5', boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}
    >
      <span>A new version of the portal is available.</span>
      <button
        onClick={() => window.location.reload()}
        style={{
          background: '#fff', color: '#2E6CB5', border: 'none', borderRadius: '6px',
          padding: '4px 12px', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
        }}
      >
        Reload
      </button>
    </div>
  );
}
